import { useEffect, useMemo } from 'react'
import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { GetHeroBannersQuery } from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { HERO_BANNER_FRAGMENT, HeroBannerData, HeroInput, isHeroBannerData } from './types'

const GET_HERO_BANNERS = gql`
  ${HERO_BANNER_FRAGMENT}
  query getHeroBanners {
    heroBanners {
      content {
        id
        attribution
        banner {
          ...HeroBannerData
        }
      }
    }
  }
`

type RecommendedHeroBannerData = {
  loading: boolean
  refetch: () => void
  attribution?: unknown
  heroBanners: HeroBannerData[]
}

export const useRecommendedHeroBanners = (): RecommendedHeroBannerData => {
  const { data, loading, refetch, error } = useQuery<GetHeroBannersQuery>(GET_HERO_BANNERS, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (error) {
      logger().error('Error fetching hero banners', { error })
    }
  }, [error])

  const heroBanners = useMemo((): HeroBannerData[] => {
    // Only log if not loading and still no content
    if (!loading && !data?.heroBanners?.content) {
      logger().warn('No hero banners available', { data })
      return []
    }

    const banners =
      data?.heroBanners?.content
        ?.map((content): HeroInput => {
          if (content?.banner) {
            return {
              recommendationId: content.id,
              attribution: content.attribution,
              ...content.banner,
            }
          }
          return null
        })
        .filter(isHeroBannerData) ?? []

    if (!loading && banners.length === 0) {
      logger().warn('No hero banners available after filtering', { data })
    }

    return banners
  }, [data, loading])

  return {
    loading,
    heroBanners,
    refetch,
  }
}

export const STREAMING_CONTENT_TYPES = ['ContentSeries', 'ContentMovie', 'ContentSpecial'] as const

export type StreamingContentType = (typeof STREAMING_CONTENT_TYPES)[number]
